import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { CATEGORIES, FALLBACK_PROJECTS } from "@/data/site";
import { Reveal, SectionHeading } from "@/components/Reveal";
import ProjectCard from "@/components/ProjectCard";
import ProjectLightbox from "@/components/ProjectLightbox";

export default function Projects() {
  const [projects, setProjects] = useState(FALLBACK_PROJECTS);
  const [filter, setFilter] = useState("All");
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    api.get("/projects").then((r) => setProjects(r.data)).catch(() => {});
  }, []);

  const filtered = useMemo(
    () => (filter === "All" ? projects : projects.filter((p) => p.category === filter)),
    [projects, filter]
  );

  return (
    <div data-testid="projects-page" className="bg-ivory">
      <section className="noise-overlay bg-obsidian pb-20 pt-40">
        <div className="mx-auto max-w-7xl px-6 sm:px-12 lg:px-20">
          <SectionHeading
            light
            align="left"
            eyebrow="Signature Projects"
            title="A Portfolio Carved in Stone"
            description="Residential, hospitality, corporate, religious and architectural works — presented like the pages of an architecture magazine."
          />
          <Reveal delay={0.15}>
            <div className="mt-10 flex flex-wrap gap-3" data-testid="project-filters">
              {CATEGORIES.map((c) => (
                <button
                  key={c}
                  onClick={() => setFilter(c)}
                  data-testid={`filter-${c.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                  className={`rounded-full px-5 py-2.5 text-[11px] font-semibold uppercase tracking-[0.18em] transition-all duration-300 ${
                    filter === c
                      ? "bg-gold text-obsidian shadow-[0_0_20px_rgba(212,175,55,0.35)]"
                      : "border border-gold/30 text-stone-300 hover:border-gold hover:text-gold"
                  }`}
                >
                  {c}
                </button>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      <section className="py-20 sm:py-24">
        <div className="mx-auto max-w-7xl px-6 sm:px-12 lg:px-20">
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {filtered.map((p, i) => (
              <Reveal key={p.id} delay={(i % 3) * 0.1} className={i % 5 === 0 ? "md:col-span-2" : ""}>
                <ProjectCard project={p} onSelect={setSelected} className="h-full" />
              </Reveal>
            ))}
          </div>
          {filtered.length === 0 && (
            <p className="py-20 text-center font-serif text-2xl italic text-stone-500" data-testid="projects-empty">
              No projects in this category yet.
            </p>
          )}
        </div>
      </section>

      <ProjectLightbox project={selected} onClose={() => setSelected(null)} />
    </div>
  );
}
